import React from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import {
  EmptyState, ListTable, Panel, SearchField, StatusChip, ToolbarButton, type ListColumn,
} from '../../ui-kit';
import { fetchCarriers, saveCarriers, type Carrier } from '../../services/carriersService';
import { matches } from './rocco/live';

/**
 * Who we ring for each carrier, and where that can be changed.
 *
 * Shown on more than one agent's configuration screen and editable from any of
 * them, which is why it sits here rather than under whichever agent needed it
 * first. There is one table: a number set on this panel is the number **every**
 * agent dials for that carrier. Two copies that drifted apart would mean a call
 * going to the wrong phone with nothing on screen to say why.
 *
 * The carrier names are the workspace's own book of business, read from its
 * warehouse — we never invent a carrier. The numbers are ours, because that
 * warehouse is read-only and could never hold them.
 *
 * Folded away by default, so a list of phone numbers is not left sitting open
 * on a shared screen.
 */
export default function CarriersPanel() {
  const [rows, setRows] = React.useState<Carrier[] | null>(null);
  const [edits, setEdits] = React.useState<Record<string, string>>({});
  const [problem, setProblem] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [missing, setMissing] = React.useState(0);
  const [query, setQuery] = React.useState('');

  const load = React.useCallback(async () => {
    try {
      const data = await fetchCarriers();
      setRows(data.carriers);
      setMissing(data.without_a_number);
      setProblem(data.warehouse_error);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not load the carriers');
      setRows([]);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  // Only what was actually typed in gets sent, so saving one number cannot
  // quietly rewrite a number somebody else changed while this page was open.
  const changed = React.useMemo(
    () => (rows ?? [])
      .filter((row) => edits[row.name] !== undefined && edits[row.name] !== (row.phone ?? ''))
      .map((row) => ({ name: row.name, phone: edits[row.name] })),
    [rows, edits],
  );

  const save = async () => {
    setSaving(true);
    setProblem(null);
    setSaved(false);
    try {
      const data = await saveCarriers(changed);
      setRows(data.carriers);
      setMissing(data.without_a_number);
      setEdits({});
      setSaved(true);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save the numbers');
    } finally {
      setSaving(false);
    }
  };

  // Searching narrows what is drawn, never what is saved: a number typed on
  // one page of results is still in `changed` after a search clears it away.
  const shown = React.useMemo(
    () => (rows ?? []).filter((row) => matches(query, [row.name, row.phone])),
    [rows, query],
  );

  const columns: ListColumn<Carrier>[] = React.useMemo(() => [
    {
      key: 'carrier',
      header: 'Carrier',
      grow: 1.2,
      minWidth: 140,
      sortValue: (row) => row.name,
      render: (row) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
          {row.name}
        </Typography>
      ),
    },
    {
      key: 'rings',
      header: 'Rings',
      grow: 1,
      minWidth: 180,
      render: (row) => {
        const value = edits[row.name] ?? row.phone ?? '';
        const isChanged = edits[row.name] !== undefined && edits[row.name] !== (row.phone ?? '');
        return (
          <TextField
            size="small"
            fullWidth
            value={value}
            placeholder="No number, never called"
            onChange={(event) => setEdits((was) => ({ ...was, [row.name]: event.target.value }))}
            inputProps={{ 'aria-label': `Number for ${row.name}`, spellCheck: false }}
            sx={(t) => ({
              '& .MuiInputBase-input': {
                fontVariantNumeric: 'tabular-nums',
                color: t.tokens.text.primary,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isChanged ? t.tokens.semantic.warning : t.tokens.border.subtle,
              },
            })}
          />
        );
      },
    },
  ], [edits]);

  const subtitle = rows === null ? 'Reading the carriers…'
    : missing > 0
      ? `${rows.length} carriers · ${missing} with no number, which the agent will not call`
      : `${rows.length} carriers, each with a number · edit a number and press Save`;

  return (
    <Panel
      title="Who we call at each carrier"
      subtitle={subtitle}
      info="The carriers come from your own policies. The number beside each one is what every
            agent dials to reach that carrier. Change it here and it changes for all of them.
            A carrier left blank is never called: the agent stops and says it has no number,
            rather than ringing someone else."
      collapsible
      // Always opens folded, deliberately without a storageKey: remembering
      // that somebody once expanded it would leave a long list of phone
      // numbers open on the screen the next time the page is shown.
      defaultCollapsed
      loading={rows === null}
      loadingHeight={320}
      // Save is always here, greyed until there is something to save. Nothing
      // is written as you type: a half-typed number is not a number, and a
      // field that saves itself would dial whatever you passed through on the
      // way. The button says plainly that nothing has changed yet.
      search={<SearchField value={query} onChange={setQuery} placeholder="Search carriers…" />}
      actions={(
        <Stack direction="row" spacing={1} alignItems="center">
          {changed.length > 0 ? (
            <Typography
              variant="caption"
              // Two words that must stay on one line: wrapped to "1 / changed"
              // it reads as a broken label rather than a count.
              sx={(t) => ({ color: t.tokens.text.tertiary, whiteSpace: 'nowrap', flexShrink: 0 })}
            >
              {changed.length} changed
            </Typography>
          ) : saved ? <StatusChip tone="good" label="Saved" /> : null}
          {changed.length > 0 ? (
            <ToolbarButton variant="ghost" onClick={() => setEdits({})} disabled={saving}>
              Undo
            </ToolbarButton>
          ) : null}
          <ToolbarButton onClick={save} disabled={saving || changed.length === 0}>
            {saving ? 'Saving…' : 'Save'}
          </ToolbarButton>
        </Stack>
      )}
    >
      {problem ? (
        <Box sx={(t) => ({
          mb: 1.5, p: 1.25, borderRadius: `${t.ds.radius.sm}px`,
          bgcolor: t.tokens.semantic.dangerSoft, color: t.tokens.text.primary,
        })}>
          <Typography variant="body2">{problem}</Typography>
        </Box>
      ) : null}

      {rows && rows.length === 0 && !problem ? (
        <EmptyState message="No carriers yet. They appear here as soon as there are policies to read." />
      ) : null}

      {rows && rows.length > 0 ? (
        <ListTable
          columns={columns}
          rows={shown}
          getRowKey={(row) => row.name}
          pageSize={10}
          defaultSort={{ key: 'carrier', direction: 'asc' }}
          emptyMessage={`No carrier matches "${query}". Clear the search to see them all.`}
        />
      ) : null}
    </Panel>
  );
}
